import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomizedCard from "../containers/_subscription/customizedCard";
import DeleteConfirmation from "../containers/_subscription/deleteConfirmation";
import HelpCard from "../containers/_subscription/helpCard";
import OtherDetails from "../containers/_subscription/otherDetails";
import SmallCard from "../containers/_subscription/smallCard";
import SubDetails from "../containers/_subscription/subDetails";
import Layout from "../layouts/layout";
import SUBSCRIPTION from "../api/services/SUBSCRIPTION";
import styles from "../styles/_screens/subscription.module.scss";
import Loader from "../components/loader";
import SelectPlan from "../components/selectPlan";
import Payment from "../components/payment";
import { paths } from "../routes/paths";

// The WhatsApp AI receptionist add-on (backend: services/waBot/constants.js).
const WA_BOT_ADDON_KEY = "whatsapp_bot";

const Subscription = () => {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [data, setData] = useState(null);

  const [selectedPlan, setSelectedPlan] = useState(null);

  // FETCH BUSINESS SUBSCRIPTION DETAILS
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await SUBSCRIPTION.get_sub_details();
        setData(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, []);

  if (!data) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  let { appointments, clients, currentPlan, subscription, messages, addOns } = data;

  // Active paid add-ons (e.g. WhatsApp bot) billed on top of the base plan.
  const activeAddOns = Array.isArray(addOns)
    ? addOns.filter((a) => a && a.active !== false)
    : [];

  // Some packages (Pro) BUNDLE the AI receptionist instead of selling it as a
  // paid add-on — backend flag `plan.services.waBot.included`. The plan doc
  // reaches us as `currentPlan` (basePlanId, or the customised planDetails
  // snapshot spread over it — same resolution the backend entitlement uses), so
  // read it from there and only fall back to basePlanId when currentPlan has no
  // services block at all.
  //
  // NOT-YET-DEPLOYED FIELD: on the live backend `services.waBot` is simply
  // absent. Every read below is optional and the flag resolves to false, which
  // renders this screen exactly as it renders today — no gaps, no NaN.
  const planServices =
    (currentPlan && currentPlan.services) ||
    (subscription &&
      subscription.basePlanId &&
      subscription.basePlanId.services) ||
    null;
  const planWaBot = planServices ? planServices.waBot : null;
  // messageQuota is deliberately never rendered — product decision: no
  // message-count number in customer-facing copy.
  const waBotIncludedInPlan =
    planWaBot === true || !!(planWaBot && planWaBot.included);

  // A business whose package already includes the receptionist must never see
  // it as a billable line — not even when a legacy paid add-on row is still
  // sitting on the subscription. It is shown as included instead, with no price.
  const billableAddOns = waBotIncludedInPlan
    ? activeAddOns.filter((a) => a && a.key !== WA_BOT_ADDON_KEY)
    : activeAddOns;

  const formatAddOnDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("he-IL");
    } catch (_) {
      return "";
    }
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>
            <div className={styles.right}>
              <SubDetails
                setIsDeleteModalOpen={setIsDeleteModalOpen}
                setIsPlanModalOpen={setIsPlanModalOpen}
                currentPlan={currentPlan}
                subscription={subscription}
              />
              {/* Plan-bundled AI receptionist — sits directly under the current
                  plan card, because it is part of the package, not a purchase. */}
              {waBotIncludedInPlan && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 16,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    padding: 20,
                    marginTop: 16,
                    direction: "rtl",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#166534",
                        background: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 999,
                        padding: "4px 10px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      כלול בחבילה
                    </span>
                    <span
                      style={{ fontSize: 16, fontWeight: 700, color: "#14532d" }}
                    >
                      👩‍💼 מזכירת AI בוואטסאפ — כלולה בחבילה ללא עלות נוספת
                    </span>
                  </div>

                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: "#166534",
                      margin: "10px 0 0 0",
                    }}
                  >
                    המזכירה עונה ללקוחות בוואטסאפ 24/7, קובעת תורים לפי הזמינות
                    האמיתית ביומן, מזיזה ומבטלת — הכול אוטומטית. אין צורך להוסיף
                    תוסף בתשלום: היא כבר חלק מהחבילה שלכם.
                  </p>

                  <button
                    type="button"
                    onClick={() => navigate(paths.whatsappBot)}
                    style={{
                      marginTop: 14,
                      background: "#16a34a",
                      color: "#fff",
                      border: "none",
                      borderRadius: 10,
                      padding: "10px 18px",
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    מעבר להגדרות המזכירה
                  </button>
                </div>
              )}

              <OtherDetails
                appointments={appointments}
                clients={clients}
                currentPlan={currentPlan}
                subscription={subscription}
                messages={messages}
              />

              {(billableAddOns.length > 0 || waBotIncludedInPlan) && (
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 16,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    padding: 20,
                    marginTop: 16,
                    direction: "rtl",
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: 12,
                    }}
                  >
                    {billableAddOns.length > 0 ? "תוספים בתשלום" : "תוספים בחבילה"}
                  </div>

                  {/* Plan-included receptionist: a real line on the bill, with
                      no charge attached to it. */}
                  {waBotIncludedInPlan && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "10px 14px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 10,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{ fontSize: 14, fontWeight: 600, color: "#16a34a" }}
                      >
                        מזכירת AI בוואטסאפ — כלולה בחבילה ללא עלות נוספת
                      </span>
                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#166534",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ללא חיוב
                      </span>
                    </div>
                  )}

                  {billableAddOns.map((a) => (
                    <div
                      key={a.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "10px 14px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 10,
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#16a34a" }}>
                        {a.title || "תוסף"}
                        {a.addedAt ? ` · פעיל מאז ${formatAddOnDate(a.addedAt)}` : ""}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap" }}>
                        ₪{a.price}/חודש
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.row}>
                <HelpCard />
                <CustomizedCard />
              </div>
            </div>
            <div className={styles.left}>
              <SmallCard heading="חיובים וחשבוניות" text="מעבר לצפייה" link={paths.invoices} />
              <SmallCard heading="היסטוריית פעולות" text="מעבר לצפייה" link={paths.notifications} />
            </div>
          </div>
        </div>
      </div>

      {isDeleteModalOpen && (
        <DeleteConfirmation setIsOpen={setIsDeleteModalOpen} />
      )}

      {isPlanModalOpen && (
        <SelectPlan
          setIsOpen={setIsPlanModalOpen}
          setIsPaymentModalOpen={setIsPaymentModalOpen}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
        />
      )}

      {isPaymentModalOpen && (
        <Payment
          setIsOpen={setIsPaymentModalOpen}
          selectedPlan={selectedPlan}
        />
      )}
    </Layout>
  );
};

export default Subscription;
